import { PageHero, Container, Overline } from "../components/Section";
import { motion } from "framer-motion";

const MILESTONES = [
  {
    year: "Early Years",
    title: "A Vision is Planted",
    body: "A handful of faithful believers gathered in prayer, seeking a place where the gospel would be preached without compromise. From their devotion, Sixth Mount Zion was born.",
    images: [
      { src: "/images/history/scan-10078.jpg", caption: "Founding members & early gatherings" },
      { src: "/images/history/smzbt-old-01.jpg", caption: "An early portrait of the congregation" },
    ],
  },
  {
    year: "Mid-Century",
    title: "A House of Worship Rises",
    body: "As the congregation grew, the community came together to build a sanctuary that would stand as a beacon of hope in Hampton — a true house of prayer for all people.",
    images: [
      { src: "/images/history/old-church-01.jpg", caption: "The original sanctuary on Butternut Drive" },
      { src: "/images/history/old-church-02.jpg", caption: "The building that gathered generations" },
    ],
  },
  {
    year: "A Place to Call Home",
    title: "Houses of Hospitality",
    body: "Beyond Sunday morning, members opened their homes for prayer meetings, fellowship dinners, and Bible study — the spaces where families became one family in Christ.",
    images: [
      { src: "/images/history/house-01.jpg", caption: "A home that hosted prayer and fellowship" },
      { src: "/images/history/house-04.jpg", caption: "Hospitality at the heart of the church" },
    ],
  },
  {
    year: "Late 20th Century",
    title: "Ministries Multiply",
    body: "Choirs, missions, youth programs, men's and women's ministries blossomed, and Sixth Mount Zion became known throughout the region for vibrant worship and outreach.",
    images: [
      { src: "/images/history/old-church-03.jpg", caption: "Worship inside the historic sanctuary" },
      { src: "/images/history/old-church-04.jpg", caption: "Pulpit ministry & gospel preaching" },
    ],
  },
  {
    year: "21st Century",
    title: "Where Miracles Live",
    body: "Anchored in tradition yet reaching into the future — digital ministry, e-campus, and global missions expand our reach, while our heart remains: loving God, loving others, and serving the world.",
    images: [
      { src: "/images/history/smzbt-01.jpg", caption: "A growing congregation, a growing vision" },
      { src: "/images/history/smzbt-02.jpg", caption: "Sixth Mount Zion today" },
    ],
  },
  {
    year: "2026",
    title: "The Year to Serve",
    body: "We declare this year a season of consecrated service — to one another, to our city of Hampton, and to the nations. Serve the Lord with gladness.",
    images: [],
  },
];

export default function History() {
  return (
    <div data-testid="history-page">
      <PageHero
        overline="Church History"
        title={<>Generations of <em className="text-gold-light font-light italic">Faith</em>, Anchored in Hampton.</>}
        kicker="The story of Sixth Mount Zion is a story of grace — passed from one generation to the next."
        image="/images/branding/sanctuary-wide.jpg"
      />

      <Container className="py-20">
        <div className="relative pl-8 md:pl-16 border-l border-wine-700 max-w-5xl">
          {MILESTONES.map((m, i) => (
            <motion.div
              key={m.title}
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.6, delay: i * 0.05 }}
              className="relative pb-20 last:pb-0"
              data-testid={`history-milestone-${i}`}
            >
              <div className="absolute -left-[42px] md:-left-[74px] top-0 w-4 h-4 bg-gold rounded-full ring-4 ring-wine-950" />
              <Overline>{m.year}</Overline>
              <h3 className="font-serif text-3xl md:text-4xl font-bold mb-5">{m.title}</h3>
              <p className="text-white/75 leading-relaxed text-lg max-w-2xl">{m.body}</p>

              {m.images.length > 0 && (
                <div className="mt-8 grid sm:grid-cols-2 gap-5 max-w-3xl">
                  {m.images.map((img, idx) => (
                    <motion.figure
                      key={img.src}
                      initial={{ opacity: 0, y: 16 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.5, delay: 0.1 + idx * 0.08 }}
                      className="group"
                      data-testid={`history-image-${i}-${idx}`}
                    >
                      <div className="overflow-hidden border border-wine-700/60 bg-wine-950/40">
                        <img
                          src={img.src}
                          alt={img.caption}
                          loading="lazy"
                          className="w-full h-72 object-cover sepia-[0.15] transition-transform duration-700 group-hover:scale-105"
                        />
                      </div>
                      <figcaption className="text-xs uppercase tracking-widest text-white/55 mt-3 font-semibold">
                        {img.caption}
                      </figcaption>
                    </motion.figure>
                  ))}
                </div>
              )}
            </motion.div>
          ))}
        </div>
      </Container>
    </div>
  );
}
